import { useEffect, useRef, useState } from 'react';
import * as S from './styles';
import { evidenceFor } from './ProvenanceGraph';
import { describeLocation } from './StoragePanel';

/*
 * Add or edit one stage of a specimen's workflow.
 *
 * `stage` null means a new stage, placed by `placement` ({after, before} --
 * either or both stage ids). The record a stage produced is chosen from what
 * the bank holds for the specimen (`linked`), so nobody types an id.
 *
 * Without `onSave` -- a reader, or anyone on an archived specimen -- the
 * stage is shown, not offered for editing. That form used to be drawn for
 * them all the same, and its Save threw "onSave is not a function".
 */
export const STAGE_TYPES = ['lab_subculture', 'storage', 'dna_analysis', 'field_collection', 'custom'];
export const STAGE_STATUSES = ['planned', 'in_progress', 'done', 'skipped'];

/** [{value: "table:id", label}] for every record a stage could point at. */
export function recordOptions(linked, t) {
  if (!linked) return [];
  const out = [];
  if (linked.origin) out.push({ value: `${linked.origin.table}:${linked.origin.id}`, label: `${t(`record.${linked.origin.table}`)} ${linked.origin.code || ''}` });
  (linked.plates || []).forEach((p) => out.push({ value: `plate_culture:${p.id}`, label: `${t('record.plate_culture')} ${p.code}` }));
  (linked.slants || []).forEach((s) => out.push({ value: `mushroom_bank:${s.id}`, label: `${t('record.mushroom_bank')} ${s.code}` }));
  (linked.dna || []).forEach((d) => out.push({ value: `dna:${d.id}`, label: `${t('record.dna')} ${d.code}` }));
  (linked.runs || []).forEach((r) => out.push({ value: `dna_sequencing_runs:${r.id}`, label: `${t('record.dna_sequencing_runs')} ${r.code}` }));
  if (linked.strain) out.push({ value: `strains:${linked.strain.id}`, label: `${t('record.strains')} ${linked.strain.code}` });
  return out;
}

const blank = (stage) => ({
  stage_type: stage?.stage_type || 'custom',
  title: stage?.title || '',
  status: stage?.status || 'planned',
  occurred_at: stage?.occurred_at ? String(stage.occurred_at).slice(0, 10) : '',
  notes: stage?.notes || '',
  record: stage?.record_table ? `${stage.record_table}:${stage.record_id}` : '',
  reason: '',
});

export default function StageEditor({
  stage, placement, linked, specimen, stageTitle, busy, error, onSave, onRemove, onCancel, t,
}) {
  const [form, setForm] = useState(() => blank(stage));
  const [removing, setRemoving] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  useEffect(() => { setForm(blank(stage)); setRemoving(false); setRemoveReason(''); }, [stage]);

  // The graph is wider than a phone and this opens below it, out of sight:
  // a tap on a card seemed to do nothing. Brought into view when another
  // stage (or another place for a new one) is chosen, not on every reload.
  const box = useRef(null);
  const opened = stage ? stage.stage_id : `new:${placement?.after || ''}:${placement?.before || ''}`;
  useEffect(() => { box.current?.scrollIntoView?.({ block: 'nearest' }); }, [opened]);

  if (!onSave) {
    return stage ? (
      <StageSummary stage={stage} linked={linked} specimen={specimen} stageTitle={stageTitle}
        onCancel={onCancel} boxRef={box} t={t} />
    ) : null;
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isNew = !stage;
  const options = recordOptions(linked, t);
  const knownRecord = !form.record || options.some((o) => o.value === form.record);

  const submit = (e) => {
    e.preventDefault();
    const [recordTable, recordId] = form.record ? form.record.split(':') : [null, null];
    const payload = {
      title: form.title.trim() || undefined,
      status: form.status,
      occurred_at: form.occurred_at || null,
      notes: form.notes,
      reason: form.reason.trim() || undefined,
    };
    // Only a changed link is sent: a record this person cannot see is kept
    // as it is rather than re-checked (and refused) on every save.
    if (isNew || form.record !== blank(stage).record) {
      payload.record_table = recordTable;
      payload.record_id = recordId;
    }
    if (isNew) {
      payload.stage_type = form.stage_type;
      if (placement?.after) payload.after_stage_id = placement.after;
      if (placement?.before) payload.before_stage_id = placement.before;
    }
    onSave(payload);
  };

  return (
    <form ref={box} onSubmit={submit} style={{ ...S.card, marginTop: 12 }} data-testid="stage-editor"
      aria-label={isNew ? t('stage.newTitle') : t('stage.editTitle')}>
      <h4 style={{ margin: '0 0 10px' }}>
        {isNew ? t('stage.newTitle') : t('stage.editTitle')}
        {!isNew && <span style={{ ...S.hint, marginInlineStart: 8 }}>{stageTitle(stage)}</span>}
      </h4>
      {isNew && placement && (
        <p style={S.hint} data-testid="stage-placement">
          {placement.after && placement.before ? t('stage.placeBetween')
            : placement.after ? t('stage.placeAfter') : placement.before ? t('stage.placeBefore') : t('stage.placeLoose')}
        </p>
      )}
      <div style={S.row}>
        {isNew && (
          <label style={{ ...S.label, flex: '1 1 160px' }}>
            {t('stage.type')}
            <select value={form.stage_type} onChange={set('stage_type')} style={S.input} title={t('stage.tip.type')}
              data-testid="stage-type">
              {STAGE_TYPES.map((type) => <option key={type} value={type}>{t(`stage.types.${type}`)}</option>)}
            </select>
          </label>
        )}
        <label style={{ ...S.label, flex: '2 1 220px' }}>
          {t('stage.titleField')}
          <input value={form.title} onChange={set('title')} maxLength={120} style={S.input}
            placeholder={isNew ? t(`stage.types.${form.stage_type}`) : ''} title={t('stage.tip.title')}
            data-testid="stage-title" />
        </label>
        <label style={{ ...S.label, flex: '1 1 140px' }}>
          {t('stage.statusField')}
          <select value={form.status} onChange={set('status')} style={S.input} title={t('stage.tip.status')}
            data-testid="stage-status">
            {STAGE_STATUSES.map((s) => <option key={s} value={s}>{t(`stage.status.${s}`)}</option>)}
          </select>
        </label>
        <label style={{ ...S.label, flex: '1 1 150px' }}>
          {t('stage.date')}
          <input type="date" value={form.occurred_at} onChange={set('occurred_at')} style={S.input}
            title={t('stage.tip.date')} data-testid="stage-date" />
        </label>
      </div>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('stage.record')}
        <select value={form.record} onChange={set('record')} style={S.input} title={t('stage.tip.record')}
          data-testid="stage-record">
          <option value="">{t('stage.noRecord')}</option>
          {!knownRecord && <option value={form.record}>{stage?.record?.code || form.record}</option>}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={S.hint}>{t('stage.recordHint')}</span>
      </label>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('stage.notes')}
        <textarea value={form.notes} onChange={set('notes')} rows={2} maxLength={4000} style={S.input}
          title={t('stage.tip.notes')} data-testid="stage-notes" />
      </label>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('stage.reason')}
        <input value={form.reason} onChange={set('reason')} maxLength={2000} style={S.input}
          title={t('stage.tip.reason')} data-testid="stage-reason" />
        <span style={S.hint}>{t('stage.reasonHint')}</span>
      </label>
      {error && <p style={S.error} role="alert">{error}</p>}
      <div style={{ ...S.row, marginTop: 12, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" disabled={busy} style={S.btn()} title={t('stage.tip.save')} data-testid="stage-save">
            {busy ? t('saving') : isNew ? t('stage.add') : t('stage.save')}
          </button>
          <button type="button" onClick={onCancel} style={S.ghostBtn} title={t('tip.cancel')}>{t('cancel')}</button>
        </div>
        {!isNew && !stage.is_canonical && onRemove && !removing && (
          <button type="button" onClick={() => setRemoving(true)} style={S.btn('var(--c-danger)')}
            title={t('stage.tip.remove')} data-testid="stage-remove">{t('stage.remove')}</button>
        )}
        {!isNew && stage.is_canonical && <span style={S.hint}>{t('stage.canonicalHint')}</span>}
      </div>
      {removing && (
        <div style={{ ...S.row, marginTop: 10 }}>
          <label style={{ ...S.label, flex: '1 1 260px' }}>
            {t('stage.removeReason')}
            <input value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} style={S.input}
              title={t('stage.tip.removeReason')} data-testid="stage-remove-reason" />
          </label>
          <button type="button" disabled={busy || removeReason.trim().length < 3} style={S.btn('var(--c-danger)')}
            onClick={() => onRemove(removeReason.trim())} title={t('stage.tip.confirmRemove')}
            data-testid="stage-remove-confirm">{t('stage.confirmRemove')}</button>
        </div>
      )}
    </form>
  );
}

/*
 * One stage, for somebody who may look but not change it: what the card on
 * the graph says, at full length, plus where the specimen is kept when this
 * is its storage stage (those fields live on the specimen, not the stage).
 */
function StageSummary({ stage, linked, specimen, stageTitle, onCancel, boxRef, t }) {
  const evidence = evidenceFor(stage, linked);
  const when = stage.occurred_at ? String(stage.occurred_at).slice(0, 10) : '';
  const location = stage.stage_type === 'storage' && specimen ? describeLocation(specimen, t) : '';
  return (
    <section ref={boxRef} style={{ ...S.card, marginTop: 12 }} data-testid="stage-readonly"
      aria-labelledby="stage-readonly-title">
      <h4 id="stage-readonly-title" style={{ margin: '0 0 10px' }}>
        {stageTitle(stage)}
        <span style={{ ...S.hint, marginInlineStart: 8 }}>{t('stage.readOnly')}</span>
      </h4>
      <dl style={S.dl}>
        <dt style={S.dt}>{t('stage.type')}</dt>
        <dd style={S.dd}>{t(`stage.types.${stage.stage_type}`)}</dd>
        <dt style={S.dt}>{t('stage.statusField')}</dt>
        <dd style={S.dd}>{t(`stage.status.${stage.status}`)}</dd>
        {when && (<><dt style={S.dt}>{t('stage.date')}</dt><dd style={S.dd}>{when}</dd></>)}
        {(stage.record || evidence.length > 0) && (<>
          <dt style={S.dt}>{t('stage.record')}</dt>
          <dd style={{ ...S.dd, ...S.mono }} data-testid="stage-readonly-record">
            {stage.record
              ? `${stage.record.code}${stage.record.deleted ? ` (${t('graph.deleted')})` : ''}`
              : evidence.join(', ')}
          </dd>
        </>)}
        {location && (<>
          <dt style={S.dt}>{t('storage.location')}</dt>
          <dd style={S.dd} data-testid="stage-readonly-location">{location}</dd>
        </>)}
        {stage.notes && (<>
          <dt style={S.dt}>{t('stage.notes')}</dt>
          <dd style={{ ...S.dd, whiteSpace: 'pre-wrap' }}>{stage.notes}</dd>
        </>)}
      </dl>
      <div style={{ ...S.row, marginTop: 12, alignItems: 'center' }}>
        {/* touch-target: the coarse-pointer rule in index.css makes buttons
            44 px tall but not a plain link, which was one 14 px line. */}
        <a href="#audit-title" title={t('stage.tip.history')} data-testid="stage-history"
          className="touch-target"
          style={{ fontSize: 14, color: 'var(--c-info-fg)', display: 'inline-flex', alignItems: 'center' }}>
          {t('stage.history')}
        </a>
        <button type="button" onClick={onCancel} style={S.ghostBtn} title={t('stage.tip.close')}>
          {t('stage.close')}
        </button>
      </div>
    </section>
  );
}
